import React, { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { ENVIRONMENTS } from "../../pages/common/ScansByEnvironment";

const PushProgressModal = ({
  isOpen,
  progress,
  onClose,
  environment,
  pendingReviewCount = 0,
  onReviewNotifications,
}) => {
  const { currentStep, totalSteps, currentScan, currentJourneys, currentIssue, currentFinalize, scanName, isComplete, error, warnings } = progress;

  let modalTitle = "";
  if (isComplete) {
    modalTitle = "Push complete";
  } else if (error) {
    modalTitle = "Push failed";
  } else if (environment === ENVIRONMENTS.PRE_STAGING) {
    modalTitle = "Pushing to staging";
  } else if (environment === ENVIRONMENTS.STAGING) {
    modalTitle = "Pushing to production";
  } else {
    modalTitle = "Pushing";
  }

  const handleDialogClose = () => {
    if (isComplete || error) onClose();
  };

  const progressPercent =
    totalSteps > 0 ? Math.min((currentStep / totalSteps) * 100, 100) : 0;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        onClose={handleDialogClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto overscroll-contain">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-6 text-left shadow-xl">
        <div className="flex justify-between items-center mb-4">
          <Dialog.Title className="text-body-lg font-semibold text-text-primary">
            {modalTitle}
          </Dialog.Title>
          {(isComplete || error) && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Close push progress"
              className="rounded-lg p-1 text-text-tertiary hover:bg-surface-neutral hover:text-text-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple/30"
            >
              <XMarkIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          )}
        </div>

        {error ? (
          <div className="mb-4 text-severity-high">
            <p className="font-medium">Error:</p>
            <p className="text-body-xs">{error}</p>
          </div>
        ) : (
          <>
            {/* Progress Bar */}
            <div className="mb-4">
              <div className="mb-1 flex justify-between text-body-xs text-text-secondary">
                <span>Progress</span>
                <span>{currentStep}/{totalSteps}</span>
              </div>
              <div
                className="h-2 w-full rounded-full bg-surface-neutral-subtle"
                role="progressbar"
                aria-label="Push progress"
                aria-valuemin="0"
                aria-valuemax="100"
                aria-valuenow={Math.round(progressPercent)}
              >
                <div
                  className="h-2 rounded-full bg-accent-purple transition-all duration-300"
                  style={{ width: `${progressPercent}%` }}
                ></div>
              </div>
            </div>

            {/* Current Status */}
            <div className="space-y-2 text-body-xs" aria-live="polite">
              {currentScan && (
                <div className="flex items-center">
                  <div className="w-4 h-4 mr-2">
                    {currentScan.status === 'complete' ? (
                      <span className="text-accent-green">✓</span>
                    ) : currentScan.status === 'progress' ? (
                      <div className="h-3 w-3 animate-spin rounded-full border-b-2 border-accent-purple"></div>
                    ) : (
                      <span className="text-text-tertiary">○</span>
                    )}
                  </div>
                  <span className={currentScan.status === 'complete' ? 'text-accent-green' : 'text-text-primary'}>
                    Creating scan: {scanName}
                  </span>
                </div>
              )}

              {currentJourneys && (
                <div className="flex items-center">
                  <div className="w-4 h-4 mr-2">
                    {currentJourneys.status === 'complete' ? (
                      <span className="text-accent-green">✓</span>
                    ) : currentJourneys.status === 'skipped' ? (
                      <span className="text-text-tertiary">·</span>
                    ) : currentJourneys.status === 'progress' ? (
                      <div className="h-3 w-3 animate-spin rounded-full border-b-2 border-accent-purple"></div>
                    ) : (
                      <span className="text-text-tertiary">○</span>
                    )}
                  </div>
                  <span className={currentJourneys.status === 'complete' ? 'text-accent-green' : 'text-text-primary'}>
                    {currentJourneys.label || 'Pushing journeys'}
                  </span>
                </div>
              )}

              {currentIssue && (
                <div className="flex items-center">
                  <div className="w-4 h-4 mr-2">
                    {currentIssue.status === 'complete' ? (
                      <span className="text-accent-green">✓</span>
                    ) : currentIssue.status === 'progress' ? (
                      <div className="h-3 w-3 animate-spin rounded-full border-b-2 border-accent-purple"></div>
                    ) : (
                      <span className="text-text-tertiary">○</span>
                    )}
                  </div>
                  <span className={currentIssue.status === 'complete' ? 'text-accent-green' : 'text-text-primary'}>
                    {currentIssue.title} ({currentIssue.index}/{currentIssue.total} issues)
                  </span>
                </div>
              )}

              {currentFinalize && (
                <div className="flex items-center">
                  <div className="w-4 h-4 mr-2">
                    {currentFinalize.status === 'complete' ? (
                      <span className="text-accent-green">✓</span>
                    ) : currentFinalize.status === 'skipped' ? (
                      <span className="text-text-tertiary">·</span>
                    ) : currentFinalize.status === 'progress' ? (
                      <div className="h-3 w-3 animate-spin rounded-full border-b-2 border-accent-purple"></div>
                    ) : (
                      <span className="text-text-tertiary">○</span>
                    )}
                  </div>
                  <span className={currentFinalize.status === 'complete' ? 'text-accent-green' : 'text-text-primary'}>
                    {currentFinalize.label || 'Finalizing'}
                  </span>
                </div>
              )}
            </div>

            {warnings && warnings.length > 0 && (
              <div className="mt-3 space-y-1 border-t border-border-subtle pt-3">
                {warnings.map((w, idx) => (
                  <div key={idx} className="rounded bg-severity-medium/5 px-2 py-1 text-caption text-severity-medium">
                    {w}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {isComplete && (
          <div className="mt-4 flex items-center justify-between">
            {pendingReviewCount > 0 ? (
              <button
                type="button"
                onClick={onReviewNotifications}
                data-testid="review-emails-button"
                className="rounded-xl bg-button-primary px-4 py-2 text-body-xs font-semibold text-white hover:bg-button-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple/30"
              >
                Review pending emails ({pendingReviewCount})
              </button>
            ) : (
              <span />
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-border-default px-4 py-2 text-body-xs font-medium text-text-secondary hover:bg-surface-neutral focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-purple/30"
            >
              Done
            </button>
          </div>
        )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default PushProgressModal; 